import React, { useRef, useState } from "react";
import { Box, Typography, Avatar, Link, Tooltip } from "@mui/material";
import defaultProfilePic from "@/assets/default-icon.jpg";
import {
  PictureAsPdf,
  InsertDriveFile,
  InsertPhoto,
  VideoFile,
} from "@mui/icons-material";
import TwoFactorCodeModal from "../../TwoFactorCodeModal";
import SettingsRedirectModal from "../../SettingsRedirectModal";

function Message({ msg, role, twoFactorEnabled = false, userRole }) {
  const isOutgoing = msg.senderRole === role;
  const videoRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9); // Default landscape
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) return <InsertPhoto />;
    if (fileType.startsWith("video/")) return <VideoFile />;
    if (fileType === "application/pdf") return <PictureAsPdf />;
    if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel"
    )
      return <InsertDriveFile />;
    return <InsertDriveFile />;
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video) {
      const ratio = video.videoWidth / video.videoHeight;
      setAspectRatio(ratio);
    }
  };

  const handleFileClick = (file) => {
    // Apply 2FA to all files (both incoming and outgoing)
    if (twoFactorEnabled) {
      // Check if s3_url_path is available for 2FA verification
      if (file.s3_url_path) {
        // 2FA is enabled, show code modal for all files
        setSelectedFile(file);
        setShowTwoFactorModal(true);
      } else {
        // If no s3_url_path, open file directly (for older messages or direct URLs)
        console.log('No s3_url_path available, opening file directly');
        window.open(file.file_url, '_blank');
      }
    } else {
      // 2FA is disabled, show settings redirect modal
      setShowSettingsModal(true);
    }
  };

  const handleTwoFactorSuccess = (fileUrl) => {
    // Open the file with the verified URL after successful 2FA verification
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
    setShowTwoFactorModal(false);
    setSelectedFile(null);
  };

  const renderMedia = () => {
    if (!msg.file || !msg.file.file_url) return null;

    // Show only file name without preview for all file types
    return (
      <Box
        onClick={() => handleFileClick(msg.file)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          borderRadius: "10px",
          border: "1px solid #ddd",
          backgroundColor: isOutgoing ? "#5e5acf" : "#f2f2f8",
          color: isOutgoing ? "#fff" : "#000",
          p: 2,
          mt: 1,
          maxWidth: "100%",
          flexWrap: "wrap",
          gap: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: isOutgoing ? "#4a4a9e" : "#e8e8f0",
          }
        }}
      >
        {getFileIcon(msg.file.file_type)}
        <Tooltip title={msg.file.original_name}>
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              maxWidth: "100%",
              flexGrow: 1,
              color: "inherit",
            }}
          >
            {msg.file.original_name}
          </Typography>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={isOutgoing ? "row-reverse" : "row"}
        alignItems="flex-end"
        mb={2}
      >
        <Avatar
          src={msg.sender?.profile_picture?.file_url || defaultProfilePic}
          alt={msg.sender?.first_name}
          sx={isOutgoing ? {  bgcolor: "primary.main", ml: 1 } : { bgcolor: "primary.main", mr: 1 }}
          
        />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          sx={{
            backgroundColor: msg.message
              ? isOutgoing
                ? "#5e5acf"
                : "#f2f2f8"
              : "none",
            color: isOutgoing ? "#fff" : "#000",
            borderRadius: "10px",
            p: 2,
            maxWidth: "70%",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
          }}
        >
          {msg.message && (
            <Typography
              sx={{
                overflow: "hidden",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                maxWidth: "100%",
                overflowWrap: "break-word",
              }}
            >
              {msg.message}
            </Typography>
          )}

          {msg.file && renderMedia()}
        </Box>

        <Typography
          variant="caption"
          color="textSecondary"
          sx={isOutgoing ? { mr: 1 } : { ml: 1 }}
        >
          {msg.time}
        </Typography>
      </Box>

      {/* Two-Factor Authentication Code Modal */}
      <TwoFactorCodeModal
        open={showTwoFactorModal}
        onClose={() => setShowTwoFactorModal(false)}
        onSuccess={handleTwoFactorSuccess}
        fileName={selectedFile?.original_name}
        s3Path={selectedFile?.s3_url_path}
        role={userRole}
      />

      {/* Settings Redirect Modal */}
      <SettingsRedirectModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        userRole={userRole}
      />
    </>
  );
}

export default Message;
